import { useMemo } from 'react';

import { useWindowSize } from '@wwe-portal/ui/hooks/use-window-size';
import {
  useGetStepupLaunchHistory,
  useGetUserStepupStatus,
} from '@wwe-portal/web/providers/queries/stepup';
import { useGetStepupById } from '@wwe-portal/web/providers/queries/stepup';
import { useGetLatestStepup } from '@wwe-portal/web/providers/queries/stepup';
import { useSegmentedOfferFilter } from '@wwe-portal/web/providers/queries/offer';

export const useStepupOffer = () => {
  const { isSegmentedOffer, isLoading: isSegmentedLoading } =
    useSegmentedOfferFilter();
  const { data: latestStepup, isLoading: isLoadingLatestStepup } =
    useGetLatestStepup();
  const { data: stepupData, isLoading: isLoadingStepupData } = useGetStepupById(
    latestStepup?.stepup?.stepsUpId
  );
  const { data: userStepup, isLoading: isLoadingUserData } =
    useGetUserStepupStatus(latestStepup?.stepup?.stepsUpId);
  const { data: launchHistoryData, isLoading: isLoadingLaunchHistory } =
    useGetStepupLaunchHistory({
      stepsUpId: latestStepup?.stepup?.stepsUpId,
      slotTimer: latestStepup?.stepup?.slotTimer,
    });

  const segmentedStepupOffers = useMemo(() => {
    if (isSegmentedLoading || !stepupData?.stepup?.offers) return [];

    const filteredOffers =
      stepupData.stepup.offers.filter((offer) => offer.offerData.OffersID) ??
      [];

    return filteredOffers.some(
      (offer) => !isSegmentedOffer(offer.offerData as { OffersID: string })
    )
      ? []
      : filteredOffers;
  }, [stepupData?.stepup?.offers, isSegmentedOffer, isSegmentedLoading]);

  return {
    offers: segmentedStepupOffers,
    stepupInfo: latestStepup?.stepup,
    claimedItems: userStepup?.data.data.claimedItems ?? 0,
    availableItems: stepupData?.stepup?.offers
      ? segmentedStepupOffers.length
      : 1,
    stepupUserLoaded: userStepup != null,
    launchHistory: launchHistoryData?.launchHistory,
    isLoadingData:
      isLoadingLatestStepup ||
      isLoadingStepupData ||
      isLoadingUserData ||
      isSegmentedLoading,
    isLoadingLaunchHistory,
  };
};

export const useStepupOfferAvailability = () => {
  const now = Date.now();
  const { width } = useWindowSize();
  const { stepupInfo, claimedItems, availableItems, isLoadingData } =
    useStepupOffer();

  const { imageUrl, endDate } = stepupInfo ?? {};

  return useMemo(
    () => ({
      isLoadingData,
      stepupInfo,
      availability: Boolean(
        stepupInfo != null &&
          imageUrl &&
          endDate &&
          Date.parse(endDate) > now &&
          claimedItems < availableItems &&
          width
      ),
    }),
    [
      availableItems,
      claimedItems,
      endDate,
      imageUrl,
      isLoadingData,
      now,
      stepupInfo,
      width,
    ]
  );
};
